/**
 * トークルームのファイル一覧（ストレージビュー）用ユーティリティ。
 *
 * - メッセージ配列 → ファイル行（FileRow）へのフラット化
 * - 受領 / 送付の分類（自分のメンバーシップポジション基準）
 * - file_metadata(JSON文字列) からのサイズ抽出
 * - フォルダ階層ツリーの再構築と集計（件数・合計サイズ・最終更新）
 * - 列ソート
 */
import {
  ProjectMessage,
  ProjectMessageFile,
} from "../../../../app/services/projectMessages";
import {
  isClientGroupPosition,
  isProducerGroupPosition,
} from "../../../../utils/memberPositionUtils";
import {
  FlatFileEntry,
  FolderNode,
  flattenFolderToPaths,
} from "../../../../components/message/folderUtils";
import {
  DATE_DISPLAY_TYPE,
  formatDateText,
} from "../../../../components/date-display/DateDisplay";

// ===== 型定義 =====

/** 受領（相手グループから）/ 送付（自分グループから）タグ */
export type FileTableTag = "received" | "sent";

export type FileTableSortField = "name" | "updated" | "size";
export type FileTableSortOrder = "asc" | "desc";

export interface FileTableSortState {
  field: FileTableSortField;
  order: FileTableSortOrder;
}

/** テーブル1行分のファイル情報（元メッセージのメタ付き） */
export interface FileRow {
  file: ProjectMessageFile;
  messageId: string;
  authorId?: number;
  authorName?: string;
  /** 自分と同じ陣営（クライアント側/プロデューサー側）からのファイルか */
  isFromMyGroup: boolean;
  /** 自分が投稿したメッセージの添付か（差し替え権限判定に使用） */
  isOwnMessage: boolean;
  /** 非表示メッセージ（受領ファイルの直接アップロード置き場）の添付か。
   *  タグ分類では常に「受領」扱いにする */
  isHidden: boolean;
  /** ファイル自体の更新日時（BE の updated_at。無ければメッセージ作成日時） */
  updatedAt: string;
  sizeBytes: number | null;
}

/** メッセージ紐付けフォルダのメタ情報（空フォルダ表示・権限判定用） */
export interface FolderInfo {
  id: string;
  name: string;
  parentId: string | null;
  messageId: string;
  /** 自分と同じ陣営のメッセージに属するフォルダか */
  isFromMyGroup: boolean;
  /** 自分が投稿したメッセージのフォルダか */
  isOwnMessage: boolean;
  /** 非表示メッセージ（受領ファイルの直接アップロード置き場）のフォルダか。
   *  ファイル行と同様、タグ分類では常に「受領」扱いにする */
  isHidden: boolean;
  /** メッセージ作成日時（空フォルダの更新日表示用） */
  messageCreatedAt: string;
  /** 有効な共有リンクが存在するか（共有中バッジ用） */
  isShared: boolean;
}

/** フォルダ階層ノード（集計値付き） */
export interface FileTableFolderNode {
  id: string;
  name: string;
  parentId: string | null;
  rows: FileRow[];
  children: FileTableFolderNode[];
  depth: number;
  /** 配下全ファイル数 */
  totalCount: number;
  /** 配下のサイズ合計（既知サイズのみ。1件も無ければ null） */
  totalSize: number | null;
  /** 配下の最終更新日時 */
  latestUpdated: string | null;
  /** フォルダが属するメッセージID（フォルダ作成・削除・アップロード用） */
  messageId?: string;
  /** 自分の陣営（or 自分の投稿）のフォルダか（編集系操作の可否） */
  canEdit: boolean;
  /** 有効な共有リンクが存在するか（共有中バッジ用） */
  isShared: boolean;
}

// ===== サイズ・日時フォーマット =====

/**
 * file_metadata（JSON文字列）からサイズ(bytes)を取り出す。
 * レガシーバッチは Python dict の repr（シングルクォート）で保存している
 * 場合があるため、JSON.parse に失敗したら正規表現でフォールバックする。
 */
export const parseFileSizeBytes = (metadata?: string | null): number | null => {
  if (!metadata) return null;
  try {
    const parsed = JSON.parse(metadata);
    if (typeof parsed === "object" && parsed !== null) {
      const size = (parsed as Record<string, unknown>).size;
      if (typeof size === "number" && Number.isFinite(size)) return size;
      if (typeof size === "string" && /^\d+$/.test(size)) return Number(size);
    }
  } catch {
    // JSON でない（Python dict repr 等）→ 正規表現で抽出
  }
  const match = metadata.match(/['"]?size['"]?\s*:\s*(\d+)/);
  if (match) return Number(match[1]);
  return null;
};

/** バイト数を人が読める形式に整形する（例: 1.2 MB） */
export const formatFileSize = (bytes: number | null): string => {
  if (bytes === null || !Number.isFinite(bytes)) return "—";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    units.length - 1,
  );
  const value = bytes / Math.pow(k, i);
  return `${parseFloat(value.toFixed(1))} ${units[i]}`;
};

/**
 * ISO 日時 → soremo 共通の日時表記（`M/D H:mm`、他年は年付き）。
 * 独自にゼロ埋め整形すると画面ごとに表記がばらつくため、必ず
 * `formatDateText`（`DateDisplay` と同じ実装）を経由する。
 */
export const formatDateTime = (iso?: string | null): string =>
  formatDateText(iso, DATE_DISPLAY_TYPE.DATE_TIME) || "—";

// ===== 行データの構築 =====

/** 投稿者ポジションが自分と同じ陣営かどうか */
const isSameGroup = (
  authorPosition: string | undefined,
  myPosition: string,
): boolean => {
  if (!authorPosition) return false;
  if (isClientGroupPosition(myPosition)) {
    return isClientGroupPosition(authorPosition);
  }
  if (isProducerGroupPosition(myPosition)) {
    return isProducerGroupPosition(authorPosition);
  }
  return false;
};

/**
 * メッセージ配列から全添付ファイルの行データを作る。
 */
export const buildFileRows = (
  messages: ProjectMessage[],
  myPosition: string,
  currentUserId?: number,
): FileRow[] => {
  const rows: FileRow[] = [];
  messages.forEach((msg) => {
    if (!msg.files || msg.files.length === 0) return;
    const hidden = msg.is_hidden === true;
    // 非表示メッセージ（受領ファイルの直接アップロード置き場）は
    // プロデューサー陣営専用の整理用のため、それ以外の閲覧者には出さない。
    if (hidden && !isProducerGroupPosition(myPosition)) return;
    const fromMyGroup = isSameGroup(msg.author?.position, myPosition);
    // /api/me 由来の id とメッセージ author の id で型（文字列/数値）が揺れても
    // 判定できるよう数値に寄せて比較する
    const own =
      currentUserId != null &&
      msg.author?.id != null &&
      Number(msg.author.id) === Number(currentUserId);
    msg.files.forEach((file) => {
      rows.push({
        file,
        messageId: msg.id,
        authorId: msg.author?.id,
        authorName: msg.author?.display_name,
        isFromMyGroup: fromMyGroup,
        isOwnMessage: own,
        isHidden: hidden,
        updatedAt: file.updated_at || msg.created_at,
        sizeBytes: parseFileSizeBytes(file.file_metadata),
      });
    });
  });
  return rows;
};

/** 受領 / 送付タグでフィルターする。送付 = 自分の陣営（or 自分自身）から。
 *  非表示メッセージの添付（クライアントから他手段で受領した資料）は、
 *  投稿者がプロデューサー自身でも常に「受領」扱いにする */
export const filterRowsByTag = (
  rows: FileRow[],
  tag: FileTableTag | null,
): FileRow[] => {
  if (tag === null) return rows;
  if (tag === "sent") {
    return rows.filter(
      (r) => !r.isHidden && (r.isFromMyGroup || r.isOwnMessage),
    );
  }
  return rows.filter(
    (r) => r.isHidden || (!r.isFromMyGroup && !r.isOwnMessage),
  );
};

/**
 * メッセージ配列から全フォルダのメタ情報を作る（空フォルダ含む）。
 * BE の folders フィールドが無い（古いキャッシュ等の）場合は空になるが、
 * その場合もファイル側の folder 参照からツリーは復元される。
 */
export const buildFolderInfos = (
  messages: ProjectMessage[],
  myPosition: string,
  currentUserId?: number,
): FolderInfo[] => {
  const infos: FolderInfo[] = [];
  messages.forEach((msg) => {
    if (!msg.folders || msg.folders.length === 0) return;
    const hidden = msg.is_hidden === true;
    // 非表示メッセージ（受領ファイルの直接アップロード置き場）は
    // プロデューサー陣営専用の整理用のため、それ以外の閲覧者には出さない
    // （buildFileRows と同じ扱い）。
    if (hidden && !isProducerGroupPosition(myPosition)) return;
    const fromMyGroup = isSameGroup(msg.author?.position, myPosition);
    const own =
      currentUserId != null &&
      msg.author?.id != null &&
      Number(msg.author.id) === Number(currentUserId);
    msg.folders.forEach((folder) => {
      infos.push({
        id: folder.id,
        name: folder.name,
        parentId: folder.parent_id,
        messageId: msg.id,
        isFromMyGroup: fromMyGroup,
        isOwnMessage: own,
        isHidden: hidden,
        messageCreatedAt: msg.created_at,
        isShared: folder.is_shared ?? false,
      });
    });
  });
  return infos;
};

/** 受領 / 送付タグでフォルダをフィルターする（空フォルダの表示制御用）。
 *  非表示メッセージのフォルダは、ファイル行と同様に常に「受領」扱いにする */
export const filterFoldersByTag = (
  folders: FolderInfo[],
  tag: FileTableTag | null,
): FolderInfo[] => {
  if (tag === null) return folders;
  if (tag === "sent") {
    return folders.filter(
      (f) => !f.isHidden && (f.isFromMyGroup || f.isOwnMessage),
    );
  }
  return folders.filter(
    (f) => f.isHidden || (!f.isFromMyGroup && !f.isOwnMessage),
  );
};

// ===== ソート =====

const compareByName = (a: FileRow, b: FileRow): number =>
  (a.file.file_name || "").localeCompare(b.file.file_name || "", "ja");

/**
 * 指定列でソートする。
 * 主キーが同値の場合はファイル名の昇順で安定させる（同一メッセージの添付など
 * 更新日時が同じ行が、昇順/降順の切り替えで無意味にシャッフルされるのを防ぐ）。
 * サイズ不明（null）の行は昇順/降順にかかわらず常に末尾に置く。
 */
export const sortFileRows = (
  rows: FileRow[],
  sort: FileTableSortState,
): FileRow[] => {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    switch (sort.field) {
      case "name": {
        return dir * compareByName(a, b);
      }
      case "updated": {
        const diff =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        if (diff !== 0) return dir * diff;
        return compareByName(a, b);
      }
      case "size": {
        if (a.sizeBytes === null && b.sizeBytes === null) {
          return compareByName(a, b);
        }
        if (a.sizeBytes === null) return 1;
        if (b.sizeBytes === null) return -1;
        const diff = a.sizeBytes - b.sizeBytes;
        if (diff !== 0) return dir * diff;
        return compareByName(a, b);
      }
      default:
        return 0;
    }
  });
};

// ===== フォルダ階層ツリー =====

const MAX_FOLDER_DEPTH = 5;

/**
 * ファイル行の folder 情報とメッセージ紐付けフォルダ（空フォルダ含む）から
 * ツリーを再構築する。
 * folderUtils.buildFolderTree と同じ方針（親が見つからないフォルダはルート扱い、
 * 最大深さを超えた階層はフラット化）で、行メタと集計値を保持する。
 */
export const buildFileTableTree = (
  rows: FileRow[],
  folderInfos: FolderInfo[] = [],
): { rootFolders: FileTableFolderNode[]; rootRows: FileRow[] } => {
  const rootRows = rows.filter((r) => !r.file.folder);
  const foldered = rows.filter((r) => r.file.folder);

  const nodes = new Map<string, FileTableFolderNode>();

  // メッセージ紐付けフォルダ（空フォルダ含む）を先に登録。メタ情報を持つ
  folderInfos.forEach((info) => {
    if (nodes.has(info.id)) return;
    nodes.set(info.id, {
      id: info.id,
      name: info.name,
      parentId: info.parentId,
      rows: [],
      children: [],
      depth: 0,
      totalCount: 0,
      totalSize: null,
      latestUpdated: info.messageCreatedAt,
      messageId: info.messageId,
      canEdit: info.isFromMyGroup || info.isOwnMessage,
      isShared: info.isShared,
    });
  });

  // ファイル側の folder 参照からの補完（folders 未取得の古いデータ向け）
  foldered.forEach((row) => {
    const folder = row.file.folder!;
    if (!nodes.has(folder.id)) {
      nodes.set(folder.id, {
        id: folder.id,
        name: folder.name,
        parentId: folder.parent_id,
        rows: [],
        children: [],
        depth: 0,
        totalCount: 0,
        totalSize: null,
        latestUpdated: null,
        messageId: row.messageId,
        canEdit: row.isFromMyGroup || row.isOwnMessage,
        isShared: false,
      });
    }
    nodes.get(folder.id)!.rows.push(row);
  });

  const rootFolders: FileTableFolderNode[] = [];
  nodes.forEach((node) => {
    if (node.parentId && nodes.has(node.parentId)) {
      nodes.get(node.parentId)!.children.push(node);
    } else {
      rootFolders.push(node);
    }
  });

  // 深さ計算 + 最大深さ超過分のフラット化
  const applyDepth = (node: FileTableFolderNode, depth: number): void => {
    node.depth = depth;
    if (depth < MAX_FOLDER_DEPTH - 1) {
      node.children.forEach((child) => applyDepth(child, depth + 1));
    } else {
      const flatten = (parent: FileTableFolderNode): void => {
        parent.children.forEach((child) => {
          parent.rows.push(...child.rows);
          flatten(child);
        });
        parent.children = [];
      };
      flatten(node);
    }
  };
  rootFolders.forEach((node) => applyDepth(node, 0));

  // 集計（件数・合計サイズ・最終更新）
  const aggregate = (node: FileTableFolderNode): void => {
    // 空フォルダの場合の最終更新フォールバック（メッセージ作成日時）
    const fallbackUpdated = node.latestUpdated;

    node.children.forEach(aggregate);
    node.totalCount =
      node.rows.length +
      node.children.reduce((sum, c) => sum + c.totalCount, 0);

    const ownSizes = node.rows
      .map((r) => r.sizeBytes)
      .filter((s): s is number => s !== null);
    const childSizes = node.children
      .map((c) => c.totalSize)
      .filter((s): s is number => s !== null);
    const allSizes = [...ownSizes, ...childSizes];
    node.totalSize =
      allSizes.length > 0 ? allSizes.reduce((sum, s) => sum + s, 0) : null;

    const timestamps = [
      ...node.rows.map((r) => new Date(r.updatedAt).getTime()),
      ...node.children
        .map((c) => (c.latestUpdated ? new Date(c.latestUpdated).getTime() : NaN))
        .filter((t) => !Number.isNaN(t)),
    ].filter((t) => !Number.isNaN(t));
    node.latestUpdated =
      timestamps.length > 0
        ? new Date(Math.max(...timestamps)).toISOString()
        : fallbackUpdated;
  };
  rootFolders.forEach(aggregate);

  return { rootFolders, rootRows };
};

/**
 * フォルダノード配下の全ファイルを zip 内相対パス付きのフラット配列にする
 * （zip 一括ダウンロード用。folderUtils.flattenFolderToPaths を流用）。
 */
export const flattenFolderNodeToEntries = (
  node: FileTableFolderNode,
): FlatFileEntry[] => {
  const convert = (n: FileTableFolderNode): FolderNode => ({
    id: n.id,
    name: n.name,
    parentId: n.parentId,
    files: n.rows.map((r) => r.file),
    children: n.children.map(convert),
    depth: n.depth,
  });
  return flattenFolderToPaths(convert(node));
};

/**
 * ツリー内の各階層をソートする（破壊的）。
 * - ファイル行: 指定の列ソート
 * - フォルダ: 指定列の集計値（最終更新 / 合計サイズ / 名前）でファイルと同じ方向に
 *   ソートする。同値は名前昇順で安定させる。
 */
export const sortFileTableTree = (
  rootFolders: FileTableFolderNode[],
  sort: FileTableSortState,
): void => {
  const dir = sort.order === "asc" ? 1 : -1;
  const compareNodes = (a: FileTableFolderNode, b: FileTableFolderNode): number => {
    const byName = a.name.localeCompare(b.name, "ja");
    switch (sort.field) {
      case "updated": {
        const at = a.latestUpdated ? new Date(a.latestUpdated).getTime() : 0;
        const bt = b.latestUpdated ? new Date(b.latestUpdated).getTime() : 0;
        const diff = at - bt;
        return diff !== 0 ? dir * diff : byName;
      }
      case "size": {
        if (a.totalSize === null && b.totalSize === null) return byName;
        if (a.totalSize === null) return 1;
        if (b.totalSize === null) return -1;
        const diff = a.totalSize - b.totalSize;
        return diff !== 0 ? dir * diff : byName;
      }
      default:
        return dir * byName;
    }
  };
  const sortNodes = (nodes: FileTableFolderNode[]): void => {
    nodes.sort(compareNodes);
    nodes.forEach((node) => {
      node.rows = sortFileRows(node.rows, sort);
      sortNodes(node.children);
    });
  };
  sortNodes(rootFolders);
};
